import React from 'react';

import {
  GridColumnVisibilityModel,
  GridColumnResizeParams,
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridSortDirection,
  GridSortModel,
  GridValidRowModel,
  MuiEvent,
} from '@mui/x-data-grid';

import { useGridApiRef } from '@mui/x-data-grid-pro';

import LinearProgress from '@mui/material/LinearProgress';

import debounce from 'lodash/debounce';

import isEqual from 'lodash/isEqual';

import { useLocation, matchPath } from 'react-router-dom';

import { isAxiosError } from 'axios';

import { FieldResponse } from 'utils/api/types';
import { JSONSchemaType } from 'utils/api/modules/incidents/incidents.types';
import { EntityFieldType, holdsMomentDate } from 'utils/api/modules/entities/entities.types';

import { useLocalStorage } from 'utils/hooks/localStorage';

import API from 'utils/api';

import { VatixError } from 'components/Error/types';

import { env } from 'config';

import routes from 'core/routes';

import { EntityModules, isRegisterEntityModule, ProtectorType } from 'core/constants';

import { timezoneStore } from 'stores/Timezone';

import { Container, StyledPagination, StyledDataGrid, GridContainer } from './styles';

import CustomToolbar from './components/Toolbar/Toolbar';
import FilterMenu from './components/FilterMenu';
import ColumnsMenu from './components/ColumnsMenu';

import {
  BooleanKeys,
  ColumnDefinition,
  ColumnQueryInfo,
  FilterType,
  GridPayload,
  GridProps,
  GridProtectorType,
  GridSortingState,
  GridState,
} from './types';
import {
  defaultColumnProps,
  defaultFormatters,
  defaultRenderers,
  getFilterOperators,
  getFilters,
  getFilteringOperator,
  getSortingOperator,
} from './utils';
import { momentDateFormatter, momentDateRenderer } from './momentDate';
import NoRowsOverlay from './components/NoRowsOverlay';
import { onCacheUpdate } from './operators/cacheEvents';
import { OptionsOperator } from './operators/Options';
import Logger from 'utils/logger/Logger';
import { useStore } from 'utils/hooks/store';

const EMPTY_FILTERS: FilterType[] = [];

const REQUIRE_SORT_ORDER: GridSortDirection[] = ['asc', 'desc'];

const HYDRATION_TIMEOUT_MS = 15000;

const Grid = ({
  basicColumns,
  filters = EMPTY_FILTERS,
  header,
  onRowClick,
  onError,
  onRefreshCallback,
  renderers = defaultRenderers,
  formatters = defaultFormatters,
  sortBy,
  requireSort = false,
  dataURL,
  fieldsURL,
  checkboxSelection,
  transformData,
  showQuickFilter = true,
  showHeader = true,
  initialData,
  withoutBorderRadius = false,
  customNoRowsText,
  filteredNoRowsText,
  entity,
  testId,
  gridPagination = true,
  disableColumnSelector,
  disableColumnFilter,
  disableColumnSorting,
  disableColumnMenu,
  disableSelectAllColumns = false,
  sortingMapping,
  temporaryFilters,
  disableExtraFields,
  pageSize = 20,
  onlyBasicExport = true,
  disableExport = false,
  gridApiRef,
  getRowId,
  disableSavePreferences,
  preferencesVersion,
  method = 'GET',
  body,
  isEntityList: isEntityListProp = false,
}: GridProps): React.ReactElement => {
  const { notification } = useStore();
  const bodyKey = method === 'POST' ? JSON.stringify(body ?? {}) : '';
  const location = useLocation();
  // matchPath is exact, so this only ever sees a grid sitting at its module root. Nested entity
  // lists opt in with the prop instead — broadening the match would sweep in grids that are under an
  // entity route but aren't entity lists (audit templates, acknowledgement recipients), whose
  // backends use a django-filter OrderingFilter and 400 on a suffixed ordering key.
  const isEntityList =
    isEntityListProp ||
    Object.values(EntityModules)
      .filter(isRegisterEntityModule)
      .some((e) => {
        const match = matchPath(routes.dashboard[e].toString(), location.pathname);
        return match !== null;
      });

  const realColumns = basicColumns.filter((c) => !c.customColumn);
  const basicColumnsDefinitionWithoutCustom = realColumns.map((columnProps) => ({
    ...defaultColumnProps,
    ...columnProps,
  })) as ColumnDefinition[];
  const basicColumnsDefinition = basicColumns.map((columnProps) => ({
    ...defaultColumnProps,
    valueGetter: columnProps.renderCell ? (value: unknown) => ({ value }) : undefined,
    ...columnProps,
  })) as ColumnDefinition[];
  const basicColumnsVisibility = Object.fromEntries(
    basicColumnsDefinitionWithoutCustom.map((c) => [c.field, c.defaultVisibility !== false])
  );
  const defaultState = React.useMemo(
    (): GridState => ({
      columns: basicColumnsVisibility,
      filters,
      quickSearch: '',
      sorting: sortBy ? [sortBy] : [],
      pagination: {
        page: 0,
        pageSize,
      },
    }),
    [basicColumnsVisibility, filters, sortBy, pageSize]
  );

  const preferencesKey = preferencesVersion ? `grid_state_${dataURL}_v${preferencesVersion}` : `grid_state_${dataURL}`;
  const [state, setState, removeState] = useLocalStorage(preferencesKey, defaultState, disableSavePreferences);
  const latestStateRef = React.useRef(state);
  React.useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  // Primitive deps: `sortBy` is passed inline and this array is a fetch effect dep.
  const defaultSorting = React.useMemo(
    (): GridSortingState[] => (sortBy ? [sortBy] : []),
    [sortBy?.field, sortBy?.sort]
  );
  const effectiveSorting = requireSort && state.sorting.length === 0 ? defaultSorting : state.sorting;

  const columnsLoadedRef = React.useRef(false);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const bodyRef = React.useRef(body);
  React.useEffect(() => {
    bodyRef.current = body;
  }, [body]);
  const internalApiRef = useGridApiRef();
  const apiRef = gridApiRef || internalApiRef;
  const [data, setData] = React.useState<GridValidRowModel[] | null>(null);
  const [columnsDefinition, setColumnsDefinition] = React.useState(basicColumnsDefinitionWithoutCustom);

  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(!initialData);
  const [currentUrl, setCurrentUrl] = React.useState(dataURL);

  const [prefsHydrated, setPrefsHydrated] = React.useState(Boolean(disableSavePreferences));
  const [fieldsHydrated, setFieldsHydrated] = React.useState(!fieldsURL);

  React.useEffect(() => {
    if (disableSavePreferences) return;
    API.user
      .loadGridPreferences(preferencesKey, HYDRATION_TIMEOUT_MS)()
      .then((response) => {
        const saved = response.data.state as Partial<GridState> | null;
        if (!saved) return;
        setState((prev: GridState) => ({
          ...prev,
          ...saved,
          columns: { ...prev.columns, ...saved.columns },
        }));
      })
      .catch((err: unknown) => {
        Logger.error('Failed to load grid preferences', err);
      })
      .finally(() => setPrefsHydrated(true));
  }, []);

  React.useEffect(() => {
    // Not AbortSignal.timeout: the jsdom these tests run on does not implement it.
    const fieldsTimeout = new AbortController();
    const fieldsDeadline = setTimeout(() => {
      Logger.error('Grid custom fields request timed out');
      fieldsTimeout.abort();
    }, HYDRATION_TIMEOUT_MS);

    (async () => {
      try {
        if (!fieldsURL) {
          setColumnsDefinition(basicColumnsDefinitionWithoutCustom);
          return;
        }
        const url = new URL(fieldsURL, env.REACT_APP_API_URL);
        url.searchParams.append('limit', '1000');
        const response = await API.common.gridRequest<{ results: FieldResponse[] }>(url.toString(), {
          signal: fieldsTimeout.signal,
        })();
        const replaced = new Set(basicColumnsDefinition.map(({ replacesField }) => replacesField));
        const customColumns = (response.data.results as unknown as EntityFieldType[]).filter(
          ({ key }) => !replaced.has(key)
        );

        let newCustomColumnDefinitions: ColumnDefinition[] = [];

        newCustomColumnDefinitions = (customColumns as unknown as EntityFieldType[]).map(
          ({ key, name, protectorType, properties }) => {
            // Just extracted and reused these two:
            const column = basicColumnsDefinition.find((c) => c.field === key);
            const lookupType = properties?.lookupType as unknown as JSONSchemaType;
            const momentDate = timezoneStore.enabled && holdsMomentDate(protectorType, properties?.roles);
            const gridType = protectorType as GridProtectorType;

            return {
              ...defaultColumnProps,
              // A column is filterable when its own type has operators, not when short text does.
              filterable: getFilterOperators(protectorType, isEntityList || false, lookupType).length > 0,
              sortable: defaultColumnProps.sortable,
              field: key,
              // get headerName from basicColumns if exists
              headerName: column?.headerName || name,
              valueGetter:
                column?.valueGetter ||
                ((value: unknown) =>
                  // if the protectorType is lookup, add lookup property to the value
                  protectorType === ProtectorType.Lookup
                    ? {
                        value: {
                          ...(value as { value?: Record<string, unknown> })?.value,
                          lookup: properties?.lookupType,
                        },
                      }
                    : { value }),
              renderCell: column?.renderCell || (momentDate ? momentDateRenderer : renderers[gridType]),
              valueFormatter: column?.valueFormatter || (momentDate ? momentDateFormatter : formatters[gridType]),
              filterOperators:
                column?.pickerFilter && properties?.enum
                  ? OptionsOperator(properties.enum.map(({ value }) => ({ value, label: value })))
                  : getFilterOperators(protectorType, isEntityList || false, lookupType),
              customColumn: true,
              protectorType,
              momentDate,
            } as unknown as ColumnDefinition;
          }
        );

        setState((prevState: GridState) => ({
          ...prevState,
          columns: {
            ...prevState.columns,
            ...Object.fromEntries(
              newCustomColumnDefinitions.map(({ field }) => {
                const basic = basicColumns.find((column) => column.field === field);
                return [
                  field,
                  prevState.columns[field] ?? (basic ? basic.customColumn || basic.defaultVisibility !== false : false),
                ];
              })
            ),
          },
        }));
        if (disableExtraFields) {
          setColumnsDefinition([
            ...basicColumnsDefinition.map(
              (column) => newCustomColumnDefinitions.find((newColumn) => newColumn.field === column.field) || column
            ),
          ]);
        } else {
          setColumnsDefinition([
            ...basicColumnsDefinition.map(
              (column) => newCustomColumnDefinitions.find((newColumn) => newColumn.field === column.field) || column
            ),
            ...newCustomColumnDefinitions.filter(
              (newColumn) => !basicColumnsDefinition.some(({ field }) => field === newColumn.field)
            ),
          ]);
        }
      } catch (e) {
        if (!fieldsTimeout.signal.aborted) {
          Logger.error('Failed to load grid custom fields', e);
        }
      } finally {
        clearTimeout(fieldsDeadline);
        columnsLoadedRef.current = true;
        setFieldsHydrated(true);
      }
    })();

    return () => {
      clearTimeout(fieldsDeadline);
      fieldsTimeout.abort();
    };
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setRowCount(initialData.count);
      setData(initialData.results);
    }
  }, [initialData]);

  React.useEffect(
    () =>
      onCacheUpdate(() => {
        if (apiRef.current) {
          apiRef.current.setFilterModel({ ...apiRef.current.state.filter.filterModel });
        }
      }),
    []
  );

  const refreshData = React.useCallback(
    async (
      filtering: FilterType[],
      quickSearch: string,
      pagination: GridPaginationModel,
      sorting: GridSortingState[],
      customColumns: BooleanKeys,
      customColumnsDefinition: ColumnDefinition[],
      temporaryFiltering: FilterType[] | undefined
    ) => {
      if (initialData) {
        return;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      const getColumnByKey = (key: string): ColumnQueryInfo | null => {
        if (sortingMapping?.[key]) {
          return { nativeFilter: false, sortingField: sortingMapping[key], protectorType: undefined };
        }
        const found = customColumnsDefinition.find((c: ColumnDefinition) => c.field === key) as ColumnQueryInfo;
        if (!found) return null;
        return { ...found, protectorType: found?.protectorType };
      };

      const url = new URL(dataURL, env.REACT_APP_API_URL);

      let customColumnsEncoded: string[] = [];

      customColumnsEncoded = Object.keys(customColumns).filter(
        (key) => customColumns[key] === true && !realColumns.some((c) => key === c.field)
      );

      if (customColumnsEncoded.length) {
        url.searchParams.append('fields', customColumnsEncoded.join(','));
      }

      const keyValueFilters = Object.entries(getFilters(filtering) || {});

      const customFilters = keyValueFilters.filter(
        ([key]) => !customColumnsDefinition.find((c: ColumnDefinition) => c.field === key)?.nativeFilter
      );

      const columnOfFilter = customFilters.map(([key]) =>
        customColumnsDefinition.find((c: ColumnDefinition) => c.field === key)
      );

      if (customFilters.length) {
        const defaultOperator = '>>';

        url.searchParams.append(
          'custom',
          customFilters
            .map(([key, value], index) => {
              const column = columnOfFilter[index];
              const operator =
                isEntityList || column?.useEntityFilterOperator
                  ? getFilteringOperator(column?.protectorType as ProtectorType, column?.momentDate)
                  : defaultOperator;

              return `${encodeURIComponent(key)}${operator}${value}`;
            })
            .join(',')
        );
      }
      const getColumnNameForFiltering = (key: string): string => {
        const nativeFilter = customColumnsDefinition.find((c: ColumnDefinition) => c.field === key)?.nativeFilter;
        return nativeFilter === true ? key : (nativeFilter as unknown as string);
      };

      keyValueFilters
        .map(([key, value]) => [getColumnNameForFiltering(key), value])
        .filter(([key]) => key !== 'filters' && Boolean(key))
        .forEach(([key, value]) => url.searchParams.append(key, value as string));

      if (temporaryFiltering) {
        (temporaryFiltering as FilterType[]).forEach((filter) => {
          url.searchParams.append(filter.field, filter.value);
        });
      }

      const [sort] = sorting;

      if (sort) {
        const column = getColumnByKey(sort.field);
        const prefix = sort.sort === 'desc' ? '-' : '';
        const suffix =
          column?.protectorType && isEntityList ? getSortingOperator(column.protectorType, column.momentDate) : '';

        url.searchParams.append('ordering', `${prefix}${column?.sortingField || sort.field}${suffix}`);
      }

      url.searchParams.append('limit', pagination.pageSize.toString());
      url.searchParams.append('offset', (pagination.pageSize * pagination.page).toString());

      if (quickSearch) {
        url.searchParams.append('query', quickSearch.trim());
      }

      if (timezoneStore.enabled) {
        url.searchParams.append('time_zone', timezoneStore.readerZone);
      }

      setCurrentUrl(url.toString());
      try {
        const response = await API.common.gridRequest<unknown>(url.toString(), {
          method,
          body: method === 'POST' ? (bodyRef.current ?? {}) : undefined,
          signal: controller.signal,
        })();
        const { count, results } = transformData ? transformData(response.data) : (response.data as GridPayload);
        setRowCount(count);
        setData(results);
      } catch (e) {
        if (controller.signal.aborted) {
          return;
        }
        if (onError && isAxiosError(e) && e.response) {
          const { status } = e.response;
          if (status === 401) {
            onError(VatixError.Unauthorized);
          } else if (status === 404) {
            removeState();
            onError(VatixError.NotFound);
          } else {
            removeState();
            onError(VatixError.SystemError);
          }
          return;
        }
        if (isAxiosError(e) && !e.response) {
          Logger.warn('Grid request failed without a response', e);
          notification.enqueueErrorSnackbar('Unable to refresh the data. Check your connection and try again.');
          return;
        }
        throw e;
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    []
  );

  const debounced = React.useMemo(() => debounce(refreshData, 500), []);
  if (onRefreshCallback) {
    onRefreshCallback(() =>
      debounced(
        state.filters,
        state.quickSearch,
        state.pagination,
        effectiveSorting,
        state.columns,
        columnsDefinition,
        temporaryFilters
      )
    );
  }

  const hasFetchedRef = React.useRef(false);
  const wasGatedRef = React.useRef(!prefsHydrated || !fieldsHydrated);
  React.useEffect(() => {
    if (!prefsHydrated || !fieldsHydrated) return;
    const fetchData = hasFetchedRef.current || !wasGatedRef.current ? debounced : refreshData;
    hasFetchedRef.current = true;
    fetchData(
      state.filters,
      state.quickSearch,
      state.pagination,
      effectiveSorting,
      state.columns,
      columnsDefinition,
      temporaryFilters
    );
  }, [
    prefsHydrated,
    fieldsHydrated,
    columnsDefinition,
    state.columns,
    state.filters,
    state.quickSearch,
    effectiveSorting,
    state.pagination,
    temporaryFilters,
  ]);

  const didMountBodyEffect = React.useRef(false);
  React.useEffect(() => {
    if (method !== 'POST') return;
    if (!didMountBodyEffect.current) {
      didMountBodyEffect.current = true;
      return;
    }
    const resetPagination = { ...state.pagination, page: 0 };
    setState((prevState: GridState) => ({ ...prevState, pagination: resetPagination }));
    debounced(
      state.filters,
      state.quickSearch,
      resetPagination,
      effectiveSorting,
      state.columns,
      columnsDefinition,
      temporaryFilters
    );
  }, [bodyKey]);

  React.useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  const savePreferences = React.useMemo(
    () =>
      disableSavePreferences
        ? () => {}
        : debounce((newState: GridState): void => {
            API.user
              .saveGridPreferences(preferencesKey, newState)()
              .catch(() => {});
          }, 1000),
    []
  );

  const onFilterModelChange = (model: GridFilterModel): void => {
    if (columnsLoadedRef.current && !isEqual(getFilters(state.filters), getFilters(model.items as FilterType[]))) {
      const resetPagination = { ...state.pagination, page: 0 };
      const newState = { ...state, filters: model.items as FilterType[], pagination: resetPagination };
      setState((prevState: GridState) => ({ ...prevState, filters: newState.filters, pagination: resetPagination }));
      savePreferences(newState);
    }

    const quickSearch = model.quickFilterValues?.join(' ') as string;

    if (model.quickFilterValues && quickSearch !== state.quickSearch) {
      const newState = { ...state, quickSearch };
      setState((prevState: GridState) => ({ ...prevState, quickSearch }));
      savePreferences(newState);
    }
  };

  const onColumnVisibilityModelChange = (columns: GridColumnVisibilityModel): void => {
    const newState = { ...state, columns };
    setState((prevState: GridState) => ({ ...prevState, columns }));
    savePreferences(newState);
  };
  const onSortModelChange = (sorting: GridSortModel): void => {
    if (!columnsLoadedRef.current) return;
    const newState = { ...state, sorting: [...sorting] as GridSortingState[] };
    setState((prevState: GridState) => ({ ...prevState, sorting: newState.sorting }));
    savePreferences(newState);
  };

  const onColumnOrderChange = (): void => {
    if (!apiRef.current) return;
    const orderedFields = apiRef.current.state.columns.orderedFields;
    const newState = { ...latestStateRef.current, columnOrder: orderedFields };
    latestStateRef.current = newState;
    setState(newState);
    savePreferences(newState);
  };

  const onColumnWidthChange = (params: GridColumnResizeParams): void => {
    const newState = {
      ...latestStateRef.current,
      columnWidths: { ...(latestStateRef.current.columnWidths ?? {}), [params.colDef.field]: params.width },
    };
    latestStateRef.current = newState;
    setState(newState);
    savePreferences(newState);
  };

  // actions column should be always at the end
  const orderedColumns = React.useMemo(() => {
    const result = [...columnsDefinition];

    if (state.columnOrder?.length) {
      const orderMap = new Map(state.columnOrder.map((field, idx) => [field, idx]));
      result.sort((a, b) => {
        const aIdx = orderMap.get(a.field) ?? Infinity;
        const bIdx = orderMap.get(b.field) ?? Infinity;
        return aIdx - bIdx;
      });
    }

    const actionIdx = result.findIndex((obj) => obj.type === 'actions');
    if (actionIdx !== -1) {
      result.push(result.splice(actionIdx, 1)[0]);
    }

    return result;
  }, [columnsDefinition, state.columnOrder]);

  // Guard against MUI calling getCellParams with a stale row id when rows were
  // replaced (e.g. by a debounced quick-filter refresh) between render and click.
  // Without this, MissingRowIdError throws inside MUI's internal click handler.
  const handleRowClick: GridEventListener<'rowClick'> = (params, event, details) => {
    if (!apiRef.current?.getRow(params.id)) return;
    onRowClick?.(params, event, details);
  };

  // Keyboard users move between cells with the arrow keys, so Enter on a cell opens its row as a
  // click does. Enter inside a control in the cell (row menu, checkbox) is left to that control.
  const handleCellKeyDown: GridEventListener<'cellKeyDown'> = (params, event, details) => {
    if (!onRowClick || event.key !== 'Enter' || event.target !== event.currentTarget) return;
    if (event.repeat || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (!apiRef.current?.getRow(params.id)) return;
    onRowClick(
      apiRef.current.getRowParams(params.id),
      event as unknown as MuiEvent<React.MouseEvent<HTMLElement>>,
      details
    );
  };

  return (
    <Container $withoutBorderRadius={withoutBorderRadius} data-testid={testId}>
      {header}
      <GridContainer>
        <StyledDataGrid
          apiRef={apiRef}
          disableRowSelectionOnClick
          autoHeight
          disableVirtualization
          filterMode="server"
          showToolbar={showHeader}
          hideFooter={data?.length === 0 || !gridPagination}
          onFilterModelChange={onFilterModelChange}
          disableColumnMenu={disableColumnMenu || !showHeader}
          disableColumnFilter={disableColumnFilter}
          disableColumnSorting={disableColumnSorting}
          disableColumnSelector={disableColumnSelector}
          columnVisibilityModel={state.columns}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          sortModel={effectiveSorting}
          {...(requireSort ? { sortingOrder: REQUIRE_SORT_ORDER } : {})}
          onSortModelChange={onSortModelChange}
          onColumnOrderChange={onColumnOrderChange}
          onColumnWidthChange={onColumnWidthChange}
          rows={data || []}
          columns={orderedColumns.map((definition) => ({
            ...definition,
            ...(state.columnWidths?.[definition.field] != null
              ? { width: state.columnWidths[definition.field], flex: undefined }
              : {}),
            actions: {
              refreshData: () => {
                refreshData(
                  state.filters,
                  state.quickSearch,
                  state.pagination,
                  effectiveSorting,
                  state.columns,
                  basicColumnsDefinition,
                  temporaryFilters
                );
              },
            },
          }))}
          onRowClick={handleRowClick}
          onCellKeyDown={handleCellKeyDown}
          getRowId={getRowId || (({ uuid }) => uuid)}
          loading={loading}
          filterModel={{ items: state.filters, quickFilterValues: [state.quickSearch] }}
          pagination={gridPagination}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={state.pagination}
          onPaginationModelChange={(pagination) => {
            if (!columnsLoadedRef.current || rowCount === 0) return;
            setState((prevState: GridState) => {
              if (
                prevState.pagination.page === pagination.page &&
                prevState.pagination.pageSize === pagination.pageSize
              ) {
                return prevState;
              }
              return { ...prevState, pagination };
            });
            savePreferences({ ...state, pagination });
          }}
          slots={{
            noRowsOverlay: () => (
              <NoRowsOverlay
                entity={entity}
                customText={
                  filteredNoRowsText && (state.filters.length > 0 || state.quickSearch)
                    ? filteredNoRowsText
                    : customNoRowsText
                }
              />
            ),
            toolbar: CustomToolbar as React.ComponentType,
            loadingOverlay: () => <LinearProgress />,
            pagination: StyledPagination,
            columnsPanel: () => <ColumnsMenu disableSelectAllColumns={disableSelectAllColumns} />,
            filterPanel: FilterMenu,
          }}
          slotProps={{
            columnHeaderFilterIconButton: {
              counter: 0,
            },
            toolbar: {
              showHeader,
              showQuickFilter,
              dataURL: currentUrl,
              columnsState: state.columns,
              columnsDefinition,
              onlyBasicExport,
              disableExport,
            },
          }}
          sortingMode={initialData ? 'client' : 'server'}
          columnBufferPx={200}
          checkboxSelection={checkboxSelection}
        />
      </GridContainer>
    </Container>
  );
};

export default Grid;
